import React from 'react';
import styles from './VirtualKeyboard.module.css';

export interface VirtualKeyboardProps {
  onKeyPress: (key: string) => void;
  onEnter?: () => void;
  onDelete?: () => void;
  disabled?: boolean;
  letterStatusMap?: Record<string, 'correct' | 'present' | 'absent' | 'empty'>;
  showEnter?: boolean;
  showDelete?: boolean;
  enterDisabled?: boolean;
  deleteDisabled?: boolean;
}

export const VirtualKeyboard: React.FC<VirtualKeyboardProps> = ({
  onKeyPress,
  onEnter,
  onDelete,
  disabled = false,
  letterStatusMap = {},
  showEnter = true,
  showDelete = true,
  enterDisabled = false,
  deleteDisabled = false,
}) => {
  const handleKeyClick = (key: string) => {
    if (disabled) return;
    onKeyPress(key);
  };

  const handleEnter = () => {
    if (disabled || enterDisabled) return;
    if (onEnter) {
      onEnter();
    } else if (onKeyPress) {
      onKeyPress('ENTER');
    }
  };

  const handleDelete = () => {
    if (disabled || deleteDisabled) return;
    if (onDelete) {
      onDelete();
    } else if (onKeyPress) {
      onKeyPress('DEL');
    }
  };

  return (
    <div className={styles.keyboard} aria-label="virtual keyboard">
      <div className={styles.keyRow}>
        {['Q','W','E','R','T','Y','U','I','O','P'].map((k) => (
          <button
            key={k}
            type="button"
            className={[
              styles.key,
              letterStatusMap[k.toLowerCase()] === 'correct' ? styles.correct :
              letterStatusMap[k.toLowerCase()] === 'present' ? styles.present :
              letterStatusMap[k.toLowerCase()] === 'absent' ? styles.absent : ''
            ].join(' ')}
            onClick={() => handleKeyClick(k)}
            disabled={disabled}
            aria-label={`key ${k}`}
          >
            {k}
          </button>
        ))}
      </div>
      <div className={styles.keyRow}>
        {['A','S','D','F','G','H','J','K','L'].map((k) => (
          <button
            key={k}
            type="button"
            className={[
              styles.key,
              letterStatusMap[k.toLowerCase()] === 'correct' ? styles.correct :
              letterStatusMap[k.toLowerCase()] === 'present' ? styles.present :
              letterStatusMap[k.toLowerCase()] === 'absent' ? styles.absent : ''
            ].join(' ')}
            onClick={() => handleKeyClick(k)}
            disabled={disabled}
            aria-label={`key ${k}`}
          >
            {k}
          </button>
        ))}
      </div>
      <div className={styles.keyRow}>
        {showEnter && (
          <button
            type="button"
            className={[styles.key, styles.keyLarge].join(' ')}
            onClick={handleEnter}
            disabled={disabled || enterDisabled}
            aria-label="enter"
          >
            Enter
          </button>
        )}
        {['Z','X','C','V','B','N','M'].map((k) => (
          <button
            key={k}
            type="button"
            className={[
              styles.key,
              letterStatusMap[k.toLowerCase()] === 'correct' ? styles.correct :
              letterStatusMap[k.toLowerCase()] === 'present' ? styles.present :
              letterStatusMap[k.toLowerCase()] === 'absent' ? styles.absent : ''
            ].join(' ')}
            onClick={() => handleKeyClick(k)}
            disabled={disabled}
            aria-label={`key ${k}`}
          >
            {k}
          </button>
        ))}
        {showDelete && (
          <button
            type="button"
            className={[styles.key, styles.keyLarge].join(' ')}
            onClick={handleDelete}
            disabled={disabled || deleteDisabled}
            aria-label="delete"
          >
            Del
          </button>
        )}
      </div>
    </div>
  );
};

export default VirtualKeyboard;
